import { AWAITING_REPLY } from "../../../../guests/constants";
import EventDetails from "../EventDetails";
import type { Guest, GuestResponse, RSVPContact, RSVPEvent } from "../types";
import WeddingEventGuest from "./WeddingEventGuest";
import { css } from "@emotion/react";
import { theme, Divider } from "@minted/minted-components";
import React from "react";

type Props = {
  contact: RSVPContact;
  events: RSVPEvent[];
  guests: Guest[];
  guestsResponses: Record<number, Record<number, GuestResponse>>;
  isMobile: boolean;
  updatedGuestNames: Record<number, { firstName?: string; lastName?: string }>;
};

const eventsGuestsSummaryStyles = {
  dividerWrapper: css`
    margin-top: ${theme.spacing.x5};
    margin-bottom: 0;
  `,
  eventGuestWrapper: css`
    background-color: ${theme.colors.gray200};
    display: grid;
    gap: ${theme.spacing.x5};
    margin-bottom: ${theme.spacing.x4};
    padding: ${theme.spacing.x4};
    ${theme.media.lessThan("medium")`
      padding: ${theme.spacing.x5};
    `};
  `,
  headerWrapper: css`
    text-align: center;
    padding-top: ${theme.spacing.x12};
    padding-bottom: ${theme.spacing.x5};
    margin-left: ${theme.spacing.x5};
    margin-right: ${theme.spacing.x5};
    ${theme.media.lessThan("medium")`
      margin-left: ${theme.spacing.x4};
      margin-right: ${theme.spacing.x4};
    `};
  `,
  headingText: css`
    ${theme.typeStyles.titleExtraLargeStyles};
  `,
  subHeadingText: css`
    ${theme.typeStyles.bodySmallStyles};

    margin-bottom: ${theme.spacing.x3};
  `,
};

const WeddingEventsGuestsSummary = ({
  contact,
  events,
  guests,
  guestsResponses,
  isMobile,
  updatedGuestNames,
}: Props) => {
  return (
    <div data-cy="eventsGuestsSummary">
      <div css={eventsGuestsSummaryStyles.headerWrapper}>
        <div css={eventsGuestsSummaryStyles.subHeadingText}>
          Thank you for responding!
        </div>
        <div
          css={eventsGuestsSummaryStyles.headingText}
          data-cy="eventsGuestsSummaryHeadingText"
        >
          {`Event Summary for ${contact.name}`}
        </div>

        <div css={eventsGuestsSummaryStyles.dividerWrapper}>
          <Divider spacing="none" type="light" />
        </div>
      </div>

      {events.map((event, index) => (
        <div key={`eventWrapper${event.id}`}>
          <EventDetails
            calendarUrls={event.calendarUrls}
            event={event}
            index={index}
            isMobile={isMobile}
            key={`summaryEvent${event.id}`}
            readOnly
            totalEvents={events.length}
          />

          <div css={eventsGuestsSummaryStyles.eventGuestWrapper}>
            {guests
              .filter((guest) => event.guests.includes(guest.id))
              .map((guest) => (
                <WeddingEventGuest
                  guest={guest}
                  guestResponse={
                    guestsResponses[event.id]?.[guest.id] ?? {
                      answers: {},
                      responseType: AWAITING_REPLY,
                    }
                  }
                  key={`summaryGuest${guest.id}`}
                  questions={event.questions}
                  readOnly
                  updatedGuestName={updatedGuestNames[guest.id] ?? {}}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WeddingEventsGuestsSummary;
